import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const MCP_BASE = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/mcp`;

export default function IntegrationsAdminPanel({ token, isSuperAdmin }) {
  const [staffList, setStaffList] = useState([]);
  const [usage, setUsage] = useState(null);
  const [tools, setTools] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [revokingFor, setRevokingFor] = useState(null);
  const [activeSection, setActiveSection] = useState("keys");
  const [expandedTool, setExpandedTool] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadStaff();
    loadUsage();
    loadTools();
  }, []);

  const loadStaff = async () => {
    setLoadingStaff(true);
    try {
      const { data } = await axios.get(`${API}/admin/staff`, { headers });
      setStaffList(data || []);
    } catch { toast.error("خطأ في تحميل قائمة الموظفين"); }
    setLoadingStaff(false);
  };

  const loadUsage = async () => {
    setLoadingUsage(true);
    try {
      const { data } = await axios.get(`${API}/admin/api-keys/usage`, { headers });
      setUsage(data);
    } catch {}
    setLoadingUsage(false);
  };

  const loadTools = async () => {
    try {
      const { data } = await axios.get(`${MCP_BASE}/tools`);
      setTools(data.tools || []);
    } catch {}
  };

  const generateKey = async (staffId, name) => {
    setGeneratingFor(staffId);
    try {
      const { data } = await axios.post(`${API}/admin/staff/${staffId}/api-key`, {}, { headers });
      toast.success(`مفتاح API جديد لـ ${name}`, { duration: 8000 });
      // Show the key in a modal-like toast since it's shown only once
      toast.info(
        <div dir="ltr" className="font-mono text-xs break-all">
          <div className="font-bold mb-1">🔑 احفظ هذا المفتاح الآن — لن يظهر مرة أخرى</div>
          <div className="bg-gray-100 p-2 rounded select-all">{data.api_key}</div>
        </div>,
        { duration: 30000 }
      );
      loadStaff();
      loadUsage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل إنشاء المفتاح");
    }
    setGeneratingFor(null);
  };

  const revokeKey = async (staffId, name) => {
    if (!window.confirm(`هل تريد حذف مفتاح API لـ ${name}؟`)) return;
    setRevokingFor(staffId);
    try {
      await axios.delete(`${API}/admin/staff/${staffId}/api-key`, { headers });
      toast.success(`تم حذف المفتاح`);
      loadStaff();
      loadUsage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل حذف المفتاح");
    }
    setRevokingFor(null);
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("تم النسخ");
  };

  const sections = [
    { key: "keys", label: "🔑 مفاتيح API" },
    { key: "endpoints", label: "🔗 نقاط الاتصال" },
    { key: "tools", label: "🛠 الأدوات (28)" },
    { key: "usage", label: "📊 الاستخدام" },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black">🔗 AI & Integrations</h2>
          <p className="text-primary/50 text-sm mt-1">MCP Content Server · مفاتيح API · توثيق الأدوات</p>
        </div>
        <button onClick={() => { loadStaff(); loadUsage(); }}
          className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold hover:bg-primary/20 transition-all">
          ↻ تحديث
        </button>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {sections.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              activeSection === s.key
                ? "bg-primary text-secondary"
                : "bg-primary/10 text-primary hover:bg-primary/20"
            }`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── API Keys section ─────────────────────────────────────────────── */}
      {activeSection === "keys" && (
        <div>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 text-sm text-amber-800">
            <strong>⚠️ تنبيه أمان:</strong> مفاتيح API تظهر مرة واحدة فقط عند الإنشاء. لا يمكن استردادها لاحقاً — احفظها فوراً.
          </div>

          {loadingStaff ? (
            <div className="text-center py-12 text-primary/40">⏳ جارٍ التحميل...</div>
          ) : staffList.length === 0 ? (
            <div className="text-center py-12 text-primary/30">لا يوجد موظفون بعد</div>
          ) : (
            <div className="space-y-3">
              {staffList.map(s => (
                <div key={s.id} className="bg-white border border-primary/10 rounded-2xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-black text-blue-700 flex-shrink-0">
                    {(s.display_name || s.username)[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-primary">{s.display_name || s.username}</div>
                    <div className="text-xs text-primary/40">@{s.username} · {s.studio_role || "employee"}</div>
                    {s.has_api_key ? (
                      <div className="text-xs text-green-600 font-bold mt-0.5">✅ مفتاح API نشط</div>
                    ) : (
                      <div className="text-xs text-primary/30 mt-0.5">— لا يوجد مفتاح API</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.has_api_key ? (
                      <button
                        onClick={() => revokeKey(s.id, s.display_name || s.username)}
                        disabled={revokingFor === s.id || !isSuperAdmin}
                        className="text-xs bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-xl font-bold hover:bg-red-100 transition-all disabled:opacity-40">
                        {revokingFor === s.id ? "⏳" : "🗑 إلغاء"}
                      </button>
                    ) : null}
                    {isSuperAdmin && (
                      <button
                        onClick={() => generateKey(s.id, s.display_name || s.username)}
                        disabled={generatingFor === s.id}
                        className="text-xs bg-primary text-secondary px-3 py-1.5 rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-40">
                        {generatingFor === s.id ? "⏳" : s.has_api_key ? "🔄 تجديد" : "✨ إنشاء مفتاح"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Endpoints section ────────────────────────────────────────────── */}
      {activeSection === "endpoints" && (
        <div className="space-y-4">
          {[
            {
              title: "MCP Content Server",
              subtitle: "JSON-RPC over HTTP — للـ AI agents",
              color: "blue",
              items: [
                { label: "معلومات الخادم", method: "GET", path: "/mcp/info" },
                { label: "قائمة الأدوات", method: "GET", path: "/mcp/tools" },
                { label: "تشغيل أداة", method: "POST", path: "/mcp/tools/{tool_name}" },
                { label: "إحصاءات الاستخدام", method: "GET", path: "/mcp/usage" },
              ]
            },
            {
              title: "Admin REST API",
              subtitle: "نقاط الـ API الإدارية",
              color: "green",
              items: [
                { label: "إحصاءات الموظفين", method: "GET", path: "/api/admin/staff/stats" },
                { label: "سجل النشاط", method: "GET", path: "/api/admin/audit-log" },
                { label: "البلاغات", method: "GET", path: "/api/admin/reports" },
                { label: "مساعد AI", method: "POST", path: "/api/admin/ai/chat" },
                { label: "إحصاءات الاستخدام", method: "GET", path: "/api/admin/api-keys/usage" },
              ]
            }
          ].map(group => (
            <div key={group.title} className="bg-white border border-primary/10 rounded-2xl p-5">
              <div className="font-black text-primary mb-1">{group.title}</div>
              <div className="text-xs text-primary/40 mb-4">{group.subtitle}</div>
              <div className="space-y-2">
                {group.items.map((item, i) => {
                  const base = process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app";
                  const full = `${base}${item.path}`;
                  return (
                    <div key={i} className="flex items-center gap-3 bg-primary/5 rounded-xl px-3 py-2">
                      <span className={`text-xs font-black px-2 py-0.5 rounded-md ${
                        item.method === "GET" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                      }`}>{item.method}</span>
                      <span className="text-xs font-bold text-primary flex-1">{item.label}</span>
                      <code className="text-xs text-primary/50 font-mono hidden md:block">{item.path}</code>
                      <button onClick={() => copyText(full)}
                        className="text-xs text-primary/40 hover:text-primary transition-colors">
                        📋
                      </button>
                    </div>
                  );
                })}
              </div>

              {group.title === "MCP Content Server" && (
                <div className="mt-4 bg-gray-50 rounded-xl p-3">
                  <div className="text-xs font-black text-primary/60 mb-2">مثال على الاستخدام</div>
                  <pre className="text-xs text-primary/70 font-mono overflow-x-auto whitespace-pre">{`curl -X POST ${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/mcp/tools/createQuestion \\
  -H "X-API-Key: hujjah_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{"question": "ما عاصمة السعودية؟", "answer": "الرياض", "category_id": "..."}'`}</pre>
                </div>
              )}
            </div>
          ))}

          <div className="bg-white border border-primary/10 rounded-2xl p-5">
            <div className="font-black text-primary mb-3">🔐 المصادقة</div>
            <div className="space-y-2 text-sm text-primary/70">
              <div className="flex items-start gap-2">
                <span className="font-black text-primary">MCP:</span>
                <code className="font-mono text-xs bg-primary/5 px-2 py-1 rounded">X-API-Key: hujjah_sk_...</code>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-black text-primary">Admin API:</span>
                <code className="font-mono text-xs bg-primary/5 px-2 py-1 rounded">Authorization: Bearer &lt;admin_token&gt;</code>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tools section ────────────────────────────────────────────────── */}
      {activeSection === "tools" && (
        <div>
          {tools.length === 0 ? (
            <div className="text-center py-12 text-primary/30">⏳ جارٍ تحميل قائمة الأدوات...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {tools.map((tool) => {
                const catColors = {
                  questions: "#10b981", categories: "#3b82f6", media: "#f59e0b",
                  community: "#8b5cf6", reports: "#ef4444", ai: "#6366f1",
                  analytics: "#0ea5e9", obsidian: "#22c55e",
                };
                const color = catColors[tool.category] || "#6b7280";
                const isExpanded = expandedTool === tool.name;
                return (
                  <div key={tool.name}
                    className="bg-white border border-primary/10 rounded-xl overflow-hidden cursor-pointer hover:border-primary/20 transition-all"
                    onClick={() => setExpandedTool(isExpanded ? null : tool.name)}>
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-primary">{tool.name}</div>
                        <div className="text-xs text-primary/40 truncate">{tool.description}</div>
                      </div>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ color, background: color + "20" }}>
                        {tool.category}
                      </span>
                      <span className="text-primary/30 text-xs">{isExpanded ? "▲" : "▼"}</span>
                    </div>
                    {isExpanded && tool.parameters && (
                      <div className="border-t border-primary/5 p-3 bg-primary/3">
                        <div className="text-xs font-black text-primary/60 mb-2">المعاملات:</div>
                        <div className="space-y-1">
                          {Object.entries(tool.parameters).map(([key, val]) => (
                            <div key={key} className="flex items-center gap-2 text-xs">
                              <code className="font-mono text-primary/70 bg-primary/5 px-1.5 py-0.5 rounded">{key}</code>
                              <span className="text-primary/40">{typeof val === "object" ? val.description || JSON.stringify(val) : val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Usage section ────────────────────────────────────────────────── */}
      {activeSection === "usage" && (
        <div>
          {loadingUsage ? (
            <div className="text-center py-12 text-primary/40">⏳ جارٍ التحميل...</div>
          ) : !usage ? (
            <div className="text-center py-12 text-primary/30">لا توجد بيانات استخدام</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "إجمالي الطلبات", value: usage.total_calls || 0, color: "#3b82f6" },
                  { label: "هذا الشهر", value: usage.this_month || 0, color: "#10b981" },
                  { label: "موظفون نشطون", value: usage.active_keys || 0, color: "#f59e0b" },
                  { label: "إجمالي المفاتيح", value: usage.total_keys || 0, color: "#8b5cf6" },
                ].map((stat, i) => (
                  <div key={i} className="bg-white border border-primary/10 rounded-2xl p-4 text-center">
                    <div className="text-3xl font-black" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="text-xs text-primary/40 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {usage.by_staff && usage.by_staff.length > 0 && (
                <div className="bg-white border border-primary/10 rounded-2xl p-5">
                  <div className="font-black text-primary mb-4">استخدام كل موظف</div>
                  <div className="space-y-2">
                    {usage.by_staff.map((s, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-black text-primary text-sm">
                          {(s.name || "?")[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-bold text-primary">{s.name}</span>
                            <span className="text-xs font-black text-primary">{s.calls}</span>
                          </div>
                          <div className="w-full bg-primary/10 rounded-full h-1.5">
                            <div className="bg-primary rounded-full h-1.5 transition-all"
                              style={{ width: `${Math.min(100, (s.calls / (usage.total_calls || 1)) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {usage.by_tool && usage.by_tool.length > 0 && (
                <div className="bg-white border border-primary/10 rounded-2xl p-5">
                  <div className="font-black text-primary mb-4">الأدوات الأكثر استخداماً</div>
                  <div className="space-y-2">
                    {usage.by_tool.slice(0, 10).map((t, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 text-xs text-primary/30 font-black">#{i + 1}</div>
                        <code className="text-xs font-mono text-primary flex-1">{t.tool}</code>
                        <span className="text-xs font-black text-primary">{t.calls}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
